import { lazy, Suspense } from 'react';
import { Navigate } from 'react-router-dom';
import { ProtectedRoute } from './ProtectedRoute';
import { RootRedirect } from './RootRedirect';
import { AuthRedirect } from './AuthRedirect';
import {
  ForgotPasswordForm,
  LoginForm,
  OtpForm,
  RegistrationForm,
  ResetPasswordForm,
} from '@/features/auth';
import { AppLayout } from '@/components/layout';
import { Loader2 } from 'lucide-react';

// Lazy load components for code splitting
const Dashboard = lazy(() =>
  import('@/features/dashboard').then((m) => ({ default: m.Dashboard }))
);
const HSEWeeklyReportForm = lazy(() =>
  import('@/features/hse-weekly-report').then((m) => ({ default: m.HSEWeeklyReportForm }))
);
const CivilInspectionForm = lazy(() =>
  import('@/features/inspection').then((m) => ({ default: m.CivilInspectionForm }))
);
const BuildingInspectionForm = lazy(() =>
  import('@/features/inspection').then((m) => ({ default: m.BuildingInspectionForm }))
);
const ShopInspectionForm = lazy(() =>
  import('@/features/inspection').then((m) => ({ default: m.ShopInspectionForm }))
);
const JHAForm = lazy(() =>
  import('@/features/jha').then((m) => ({ default: m.JHAForm }))
);
const ToolboxMeetingForm = lazy(() =>
  import('@/features/toolbox-meetings').then((m) => ({ default: m.ToolboxMeetingForm }))
);
const SettingsPage = lazy(() =>
  import('@/features/settings').then((m) => ({ default: m.SettingsPage }))
);
const FormsLibrary = lazy(() =>
  import('@/features/forms-library').then((m) => ({ default: m.FormsLibrary }))
);
const ProjectsLandingPage = lazy(() =>
  import('@/features/crews-v2').then((m) => ({ default: m.ProjectsLandingPage }))
);
const ProjectCrewsPage = lazy(() =>
  import('@/features/crews-v2').then((m) => ({ default: m.ProjectCrewsPage }))
);
const AttendancePage = lazy(() =>
  import('@/features/attendance').then((m) => ({ default: m.AttendancePage }))
);
const SubmittedReportsList = lazy(() =>
  import('@/features/submitted-reports').then((m) => ({ default: m.SubmittedReportsList }))
);
const UserManagementPage = lazy(() =>
  import('@/features/users').then((m) => ({ default: m.UserManagementPage }))
);
// Loading fallback component - wraps in AppLayout so TopNav and Sidebar stay visible
const PageLoader = () => (
  <AppLayout>
    <div className="flex items-center justify-center py-40">
      <Loader2 className="h-10 w-10 animate-spin text-[#1a2942]" />
    </div>
  </AppLayout>
);

export const routes = [
  {
    path: '/',
    element: <RootRedirect />,
  },
  {
    path: '/login',
    element: (
      <AuthRedirect>
        <LoginForm />
      </AuthRedirect>
    ),
  },
  {
    path: '/signin',
    element: <Navigate to="/login" replace />,
  },
  {
    path: '/forgot-password',
    element: (
      <AuthRedirect>
        <ForgotPasswordForm />
      </AuthRedirect>
    ),
  },
  {
    path: '/reset-password',
    element: <ResetPasswordForm />,
  },
  {
    path: '/otp',
    element: (
      <AuthRedirect>
        <OtpForm />
      </AuthRedirect>
    ),
  },
  {
    path: '/register',
    element: (
      <AuthRedirect>
        <RegistrationForm />
      </AuthRedirect>
    ),
  },
  {
    element: <ProtectedRoute />,
    children: [
      {
        path: '/dashboard',
        element: (
          <Suspense fallback={<PageLoader />}>
            <Dashboard />
          </Suspense>
        ),
      },
      {
        path: '/forms',
        element: (
          <Suspense fallback={<PageLoader />}>
            <FormsLibrary />
          </Suspense>
        ),
      },
      {
        path: '/forms/hse-report',
        element: (
          <Suspense fallback={<PageLoader />}>
            <HSEWeeklyReportForm />
          </Suspense>
        ),
      },
      {
        path: '/forms/inspection/civil',
        element: (
          <Suspense fallback={<PageLoader />}>
            <CivilInspectionForm />
          </Suspense>
        ),
      },
      {
        path: '/forms/inspection/building',
        element: (
          <Suspense fallback={<PageLoader />}>
            <BuildingInspectionForm />
          </Suspense>
        ),
      },
      {
        path: '/forms/inspection/shop',
        element: (
          <Suspense fallback={<PageLoader />}>
            <ShopInspectionForm />
          </Suspense>
        ),
      },
      {
        path: '/forms/jha',
        element: (
          <Suspense fallback={<PageLoader />}>
            <JHAForm />
          </Suspense>
        ),
      },
      {
        path: '/forms/toolbox-meeting',
        element: (
          <Suspense fallback={<PageLoader />}>
            <ToolboxMeetingForm />
          </Suspense>
        ),
      },
      {
        path: '/approvals',
        element: <Navigate to="/reports" replace />,
      },
      {
        path: '/settings',
        element: (
          <Suspense fallback={<PageLoader />}>
            <SettingsPage />
          </Suspense>
        ),
      },
      {
        path: '/crews',
        element: (
          <Suspense fallback={<PageLoader />}>
            <ProjectsLandingPage />
          </Suspense>
        ),
      },
      {
        path: '/crews/project/:projectId',
        element: (
          <Suspense fallback={<PageLoader />}>
            <ProjectCrewsPage />
          </Suspense>
        ),
      },
      {
        path: '/attendance',
        element: (
          <Suspense fallback={<PageLoader />}>
            <AttendancePage />
          </Suspense>
        ),
      },
      {
        path: '/reports',
        element: (
          <Suspense fallback={<PageLoader />}>
            <SubmittedReportsList />
          </Suspense>
        ),
      },
      {
        path: '/submitted-reports',
        element: <Navigate to="/reports" replace />,
      },
    ],
  },
  {
    element: <ProtectedRoute requireAdmin />,
    children: [
      {
        path: '/users',
        element: (
          <Suspense fallback={<PageLoader />}>
            <UserManagementPage />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: '*',
    element: (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-4xl font-medium text-gray-800 mb-4">404</h1>
          <p className="text-gray-600">Page not found</p>
        </div>
      </div>
    ),
  },
];
